import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { useAppStore } from "./stores/appStore";
import Layout from "./components/layout/Layout";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";

// Lazy load pages for code splitting - reduces initial bundle size
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Athletes = lazy(() => import("./pages/Athletes"));
const Colleges = lazy(() => import("./pages/Colleges"));
const Matches = lazy(() => import("./pages/Matches"));
const MatchingEngine = lazy(() => import("./pages/MatchingEngine"));
const DataSync = lazy(() => import("./pages/DataSync"));
const Scraper = lazy(() => import("./pages/Scraper"));
const Pipeline = lazy(() => import("./pages/Pipeline"));
const Analytics = lazy(() => import("./pages/Analytics"));
const System = lazy(() => import("./pages/System"));

// Lazy load Jobs pages
const JobQueue = lazy(() => import("./pages/Jobs").then(m => ({ default: m.JobQueue })));
const JobHistory = lazy(() => import("./pages/Jobs").then(m => ({ default: m.JobHistory })));

// Lazy load DataQuality pages
const QualityDashboard = lazy(() => import("./pages/DataQuality").then(m => ({ default: m.QualityDashboard })));
const AthleteQuality = lazy(() => import("./pages/DataQuality").then(m => ({ default: m.AthleteQuality })));

// Lazy load Schedules
const ScheduleManager = lazy(() => import("./pages/Schedules").then(m => ({ default: m.ScheduleManager })));

// Lazy load Scraper Config pages
const ScraperSources = lazy(() => import("./pages/ScraperConfig").then(m => ({ default: m.ScraperSources })));
const ScraperSourceDetail = lazy(() => import("./pages/ScraperConfig").then(m => ({ default: m.ScraperSourceDetail })));

// Lazy load Scraper Review
const ScraperReview = lazy(() => import("./pages/ScraperReview"));

// Lazy load Resolution Cache (#532)
const ResolutionCache = lazy(() => import("./pages/ResolutionCache"));

// Lazy load Media pages (#804)
const MediaCrawler = lazy(() => import("./pages/MediaCrawler"));
const MediaApproval = lazy(() => import("./pages/MediaApproval"));

// Lazy load Data Ingestion (#678)
const DataIngestionSources = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionSources })));
const DataIngestionSourceForm = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionSourceForm })));
const DataIngestionMappings = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionMappings })));
const DataIngestionGhostFill = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionGhostFill })));
const DataIngestionUpload = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionUpload })));
const DataIngestionResolutionReview = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionResolutionReview })));
const DataIngestionUploadHistory = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionUploadHistory })));
const DataIngestionCache = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionCache })));
const DataIngestionCollegeLookups = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionCollegeLookups })));
const DataIngestionTeamAliases = lazy(() => import("./pages/DataIngestion").then(m => ({ default: m.DataIngestionTeamAliases })));

// Page loading fallback - minimal to avoid flicker
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <LoadingSpinner />
    </div>
  );
}

function App() {
  const theme = useAppStore((state) => state.theme);

  return (
    <div className={theme === "dark" ? "dark" : ""}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Pipeline />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/athletes" element={<Athletes />} />
                      <Route path="/colleges" element={<Colleges />} />
                      <Route path="/matches" element={<Matches />} />
                      <Route path="/matching" element={<MatchingEngine />} />
                      <Route path="/scraper" element={<Scraper />} />
                      <Route path="/scraper/sources" element={<ScraperSources />} />
                      <Route path="/scraper/sources/:id" element={<ScraperSourceDetail />} />
                      <Route path="/scraper/review" element={<ScraperReview />} />
                      <Route path="/scraper/resolution-cache" element={<ResolutionCache />} />
                      <Route path="/media-crawler" element={<MediaCrawler />} />
                      <Route path="/media-approval" element={<MediaApproval />} />
                      <Route path="/data-ingestion/sources" element={<DataIngestionSources />} />
                      <Route path="/data-ingestion/sources/new" element={<DataIngestionSourceForm />} />
                      <Route path="/data-ingestion/sources/:id" element={<DataIngestionSourceForm />} />
                      <Route path="/data-ingestion/sources/:id/mappings" element={<DataIngestionMappings />} />
                      <Route path="/data-ingestion/sources/:id/ghost-fill" element={<DataIngestionGhostFill />} />
                      <Route path="/data-ingestion/sources/:id/upload" element={<DataIngestionUpload />} />
                      <Route path="/data-ingestion/sources/:id/uploads/:uploadId/resolve" element={<DataIngestionResolutionReview />} />
                      <Route path="/data-ingestion/sources/:id/uploads" element={<DataIngestionUploadHistory />} />
                      <Route path="/data-ingestion/cache" element={<DataIngestionCache />} />
                      <Route path="/data-ingestion/team-aliases" element={<DataIngestionTeamAliases />} />
                      <Route path="/data-ingestion/college-lookups" element={<DataIngestionCollegeLookups />} />
                      <Route path="/pipeline" element={<Pipeline />} />
                      <Route path="/sync" element={<DataSync />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/jobs" element={<JobQueue />} />
                      <Route path="/jobs/history" element={<JobHistory />} />
                      <Route path="/quality" element={<QualityDashboard />} />
                      <Route path="/quality/athletes" element={<AthleteQuality />} />
                      <Route path="/schedules" element={<ScheduleManager />} />
                      <Route path="/system" element={<System />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </div>
  );
}

export default App;
